/**
 * In-memory shaping of a flat list for the Data fields pane: the chosen category columns (in any order) become the
 * row hierarchy, rows with the same path merge, and a "group by" column pivots the rows into category rows × the
 * column's distinct values, measures summed per cell. Produces the inputs the range parsers take
 * (`parseRangeDataView` / `parseRowHierarchyDataView`), so nothing downstream of the parser changes.
 *
 * Shared by the Excel range reader and the PowerPoint data editor; no Office objects, unit-testable.
 */

export const BLANK_GROUP_LABEL = "(blank)";

/** Value type strings as Excel's `RangeValueType` spells them; the range parsers only look for "Error" */
export const PivotCellType = {
    Double: "Double",
    String: "String",
    Empty: "Empty",
} as const;

export interface GroupByPivotInput {
    /** Category labels per data row, blank-continuation (hierarchy) or filled; one entry per category column */
    rowLabels: unknown[][];
    /** Measure cells per data row, in measure order, without the group column */
    values: unknown[][];
    /** The group column's cell per data row; without it the rows are only grouped into a hierarchy, not pivoted */
    groupCells?: unknown[];
    /** Index of the comment column among the measures, -1 when there is none */
    commentIndex?: number;
    /** Measures that carry text (first non-empty text per cell instead of a sum); the comment column needs no entry */
    textIndexes?: number[];
}

export interface GroupByPivotResult {
    /** One entry per distinct category key, first-appearance order, blank-continuation form */
    rowLabels: string[][];
    /** Distinct group labels, first-appearance order */
    groups: string[];
    /** rows × (groups × measures), null where a row has no value in a group */
    values: (number | string | null)[][];
    valueTypes: string[][];
}

/** "Total" / "Grand Total" rows of a pivot pasted as values; the visual computes its own totals */
export function isTotalLabel(label: unknown): boolean {
    return typeof label === "string" && /^(grand\s+)?total$/i.test(label.trim());
}

/** Trimmed text of a sheet or range cell; objects and nullish cells read as empty */
function cellToText(cell: unknown): string {
    if (typeof cell === "string") {
        return cell.trim();
    }
    if (typeof cell === "number" || typeof cell === "boolean") {
        return String(cell);
    }
    return "";
}

export function toGroupLabel(cell: unknown): string {
    const text = cellToText(cell);
    return text === "" ? BLANK_GROUP_LABEL : text;
}

function toNumber(cell: unknown): number | null {
    if (typeof cell === "number") {
        return isNaN(cell) ? null : cell;
    }
    if (typeof cell === "string") {
        const trimmed = cell.trim();
        if (trimmed === "") {
            return null;
        }
        const parsed = parseFloat(trimmed.replace(/,/g, ""));
        return isNaN(parsed) ? null : parsed;
    }
    return null;
}

function toText(cell: unknown): string | null {
    const text = cellToText(cell);
    return text === "" ? null : text;
}

/**
 * Carries parent labels forward so every row has its full category key. A label that stays empty (nothing above to
 * carry) becomes "(blank)": the range parser drops leaves with an empty value, which would leave a parent without
 * children and crash the view model.
 */
function fillDownLabels(rowLabels: unknown[][]): string[][] {
    const current: string[] = [];
    return rowLabels.map((row) =>
        row.map((label, level) => {
            const text = cellToText(label);
            if (level === row.length - 1) {
                // the leaf is always written out in a hierarchy; an empty one is a real blank, not a repeat
                return text === "" ? BLANK_GROUP_LABEL : text;
            }
            if (text !== "") {
                current[level] = text;
                current.length = level + 1;
            }
            return current[level] ?? BLANK_GROUP_LABEL;
        })
    );
}

/** The inverse of fill-down: a label equal to the previous row's at that level and all levels above is blanked */
function toBlankContinuation(filled: string[][]): string[][] {
    return filled.map((row, i) => {
        if (i === 0) {
            return [...row];
        }
        const previous = filled[i - 1];
        let samePath = true;
        return row.map((label, level) => {
            samePath = samePath && previous[level] === label;
            return samePath && level < row.length - 1 ? "" : label;
        });
    });
}

/**
 * Groups the rows into a hierarchy tree: rows with the same category path merge (measures summed), and the result
 * is in tree order with siblings in first-appearance order, so a hierarchy whose levels the user reordered still
 * parses as nested blocks. With `groupCells` the rows are also pivoted by that column's values (one Group level).
 */
export function shapeRows(input: GroupByPivotInput): GroupByPivotResult {
    const { rowLabels, values, groupCells } = input;
    const commentIndex = input.commentIndex ?? -1;
    const textIndexes = new Set(input.textIndexes ?? []);
    const measureCount = values[0]?.length ?? 0;

    const filled = fillDownLabels(rowLabels);
    const groups: string[] = [];
    const groupIndex = new Map<string, number>();
    const rowKeys: string[] = [];
    const rows = new Map<string, { labels: string[]; cells: Map<number, number | string>; order: number[] }>();
    /** per parent path: its children's labels in first-appearance order */
    const siblings = new Map<string, Map<string, number>>();

    filled.forEach((labels, r) => {
        if (isTotalLabel(labels[0])) {
            return;
        }
        let g = 0;
        if (groupCells) {
            const group = toGroupLabel(groupCells[r]);
            if (!groupIndex.has(group)) {
                groupIndex.set(group, groups.length);
                groups.push(group);
            }
            g = groupIndex.get(group);
        }
        const key = labels.join("\u0000");
        let row = rows.get(key);
        if (!row) {
            const order: number[] = [];
            let parentPath = "";
            labels.forEach((label) => {
                let children = siblings.get(parentPath);
                if (!children) {
                    children = new Map();
                    siblings.set(parentPath, children);
                }
                if (!children.has(label)) {
                    children.set(label, children.size);
                }
                order.push(children.get(label));
                parentPath += "\u0000" + label;
            });
            row = { labels, cells: new Map(), order };
            rows.set(key, row);
            rowKeys.push(key);
        }
        for (let m = 0; m < measureCount; m++) {
            const cell = values[r]?.[m];
            if (m === commentIndex) {
                // one comment per row, attached to the first group (same rule as cross tables)
                const text = toText(cell);
                if (text !== null && !row.cells.has(m)) {
                    row.cells.set(m, text);
                }
                continue;
            }
            if (textIndexes.has(m)) {
                // a text measure: the first non-empty text per cell, in every group
                const text = toText(cell);
                const slot = g * measureCount + m;
                if (text !== null && !row.cells.has(slot)) {
                    row.cells.set(slot, text);
                }
                continue;
            }
            const number = toNumber(cell);
            if (number === null) {
                continue;
            }
            const slot = g * measureCount + m;
            row.cells.set(slot, (<number>row.cells.get(slot) ?? 0) + number);
        }
    });

    const slotCount = Math.max(groups.length, 1) * measureCount;
    const compareOrder = (a: number[], b: number[]) => {
        for (let level = 0; level < a.length; level++) {
            if (a[level] !== b[level]) {
                return a[level] - b[level];
            }
        }
        return 0;
    };
    rowKeys.sort((a, b) => compareOrder(rows.get(a).order, rows.get(b).order));

    const resultValues: (number | string | null)[][] = [];
    const resultTypes: string[][] = [];
    rowKeys.forEach((key) => {
        const row = rows.get(key);
        const rowValues: (number | string | null)[] = [];
        const rowTypes: string[] = [];
        for (let slot = 0; slot < slotCount; slot++) {
            const cell = row.cells.get(slot);
            if (cell === undefined) {
                rowValues.push(null);
                rowTypes.push(PivotCellType.Empty);
            } else {
                rowValues.push(cell);
                rowTypes.push(typeof cell === "number" ? PivotCellType.Double : PivotCellType.String);
            }
        }
        resultValues.push(rowValues);
        resultTypes.push(rowTypes);
    });

    return {
        rowLabels: toBlankContinuation(rowKeys.map((key) => rows.get(key).labels)),
        groups,
        values: resultValues,
        valueTypes: resultTypes,
    };
}
